import { useEffect, useState } from "react";
import api from "../../lib/api";
import { Plus, Trash2, Pencil, Save, X, Copy } from "lucide-react";

const empty = {
    title: "",
    slug: "",
    location: "Juba, South Sudan",
    employment_type: "Full-time",
    department: "",
    summary: "",
    description: "",
    responsibilities: [],
    requirements: [],
    how_to_apply: "Please send your CV and a brief cover letter to the email below.",
    application_email: "",
    deadline: "",
    order: 0,
    featured: false,
    published: true,
};

const TYPES = ["Full-time", "Part-time", "Contract", "Volunteer", "Internship", "Consultancy"];

const slugify = (s) =>
    s.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 80);

export default function CareersEditor() {
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null);

    const load = () => api.get("/jobs?all=true").then(({ data }) => setItems(data));
    useEffect(() => { load(); }, []);

    const save = async (j) => {
        if (j.id) await api.put(`/jobs/${j.id}`, j);
        else await api.post("/jobs", j);
        setEditing(null);
        load();
    };
    const del = async (id) => {
        if (!window.confirm("Delete this job posting?")) return;
        await api.delete(`/jobs/${id}`);
        load();
    };
    const dup = async (id) => { await api.post(`/jobs/${id}/duplicate`); load(); };

    return (
        <div data-testid="careers-editor">
            <div className="flex items-center justify-between mb-8">
                <div>
                    <h1 className="font-display text-4xl mb-2">Careers & Jobs</h1>
                    <p className="text-muted-foreground">Post open positions, internships, and volunteer roles.</p>
                </div>
                <button
                    onClick={() => setEditing({ ...empty })}
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-sm bg-primary text-primary-foreground text-sm font-medium"
                    data-testid="add-job"
                >
                    <Plus size={14} /> New Job
                </button>
            </div>

            <div className="bg-card border border-border rounded-sm overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-muted/50 text-xs uppercase tracking-[0.18em] text-muted-foreground">
                        <tr>
                            <th className="text-left p-4">Title</th>
                            <th className="text-left p-4">Type</th>
                            <th className="text-left p-4">Location</th>
                            <th className="text-left p-4">Deadline</th>
                            <th className="text-left p-4">Published</th>
                            <th className="text-right p-4">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.length === 0 && (
                            <tr><td colSpan={6} className="p-8 text-center text-muted-foreground text-sm">No jobs posted yet.</td></tr>
                        )}
                        {items.map((j) => (
                            <tr key={j.id} className="border-t border-border">
                                <td className="p-4 font-medium">
                                    {j.title}
                                    {j.featured && <span className="ml-2 text-[10px] uppercase tracking-[0.18em] text-secondary">Featured</span>}
                                </td>
                                <td className="p-4 text-muted-foreground">{j.employment_type}</td>
                                <td className="p-4 text-muted-foreground">{j.location}</td>
                                <td className="p-4 text-muted-foreground">{j.deadline || "—"}</td>
                                <td className="p-4">{j.published ? <span className="text-secondary">● Yes</span> : <span className="text-muted-foreground">○ No</span>}</td>
                                <td className="p-4 text-right space-x-2">
                                    <button onClick={() => setEditing({ ...j })} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-sm border border-border text-xs" data-testid={`edit-job-${j.slug}`}><Pencil size={12} /> Edit</button>
                                    <button onClick={() => dup(j.id)} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-sm border border-border text-xs" data-testid={`dup-job-${j.slug}`}><Copy size={12} /> Duplicate</button>
                                    <button onClick={() => del(j.id)} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-sm border border-destructive text-destructive text-xs" data-testid={`del-job-${j.slug}`}><Trash2 size={12} /> Delete</button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {editing && <Modal item={editing} onChange={setEditing} onSave={save} onClose={() => setEditing(null)} />}
        </div>
    );
}

const Modal = ({ item, onChange, onSave, onClose }) => {
    const set = (k, v) => onChange({ ...item, [k]: v });
    const setList = (k, text) => set(k, text.split("\n").map((l) => l.trim()).filter(Boolean));

    return (
        <div className="fixed inset-0 z-50 bg-foreground/50 flex items-start justify-center p-6 overflow-y-auto">
            <div className="bg-card border border-border rounded-sm w-full max-w-3xl my-10">
                <div className="flex items-center justify-between p-6 border-b border-border">
                    <h2 className="font-display text-2xl">{item.id ? "Edit Job" : "New Job"}</h2>
                    <button onClick={onClose} className="p-2 rounded-sm hover:bg-muted"><X size={18} /></button>
                </div>
                <div className="p-6 space-y-5">
                    <F label="Title" value={item.title} onChange={(v) => onChange({ ...item, title: v, slug: item.id ? item.slug : slugify(v) })} testid="job-title" />
                    <F label="Slug" value={item.slug} onChange={(v) => set("slug", slugify(v))} testid="job-slug" />
                    <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                            <label className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-2 block">Employment type</label>
                            <select value={item.employment_type} onChange={(e) => set("employment_type", e.target.value)} className="w-full px-3 py-2.5 rounded-sm border border-border bg-background text-sm" data-testid="job-type">
                                {TYPES.map((t) => <option key={t}>{t}</option>)}
                            </select>
                        </div>
                        <F label="Department" value={item.department} onChange={(v) => set("department", v)} testid="job-department" />
                        <F label="Location" value={item.location} onChange={(v) => set("location", v)} testid="job-location" />
                        <F label="Application deadline" value={item.deadline} onChange={(v) => set("deadline", v)} testid="job-deadline" placeholder="e.g. 31 March 2026" />
                    </div>
                    <T label="Summary (1-2 lines, shown on listing)" value={item.summary} onChange={(v) => set("summary", v)} rows={2} testid="job-summary" />
                    <T label="Full description" value={item.description} onChange={(v) => set("description", v)} rows={6} testid="job-description" />
                    <T label="Key responsibilities (one per line)" value={(item.responsibilities || []).join("\n")} onChange={(v) => setList("responsibilities", v)} rows={5} testid="job-responsibilities" />
                    <T label="Requirements (one per line)" value={(item.requirements || []).join("\n")} onChange={(v) => setList("requirements", v)} rows={5} testid="job-requirements" />
                    <T label="How to apply" value={item.how_to_apply} onChange={(v) => set("how_to_apply", v)} rows={3} testid="job-howto" />
                    <F label="Application email" value={item.application_email} onChange={(v) => set("application_email", v)} testid="job-email" placeholder="careers@yacrrd-ss.org" />
                    <div className="flex gap-6 flex-wrap pt-2">
                        <label className="inline-flex items-center gap-2"><input type="checkbox" checked={!!item.featured} onChange={(e) => set("featured", e.target.checked)} data-testid="job-featured" /><span className="text-sm">Featured</span></label>
                        <label className="inline-flex items-center gap-2"><input type="checkbox" checked={!!item.published} onChange={(e) => set("published", e.target.checked)} data-testid="job-published" /><span className="text-sm">Published</span></label>
                        <label className="inline-flex items-center gap-2 ml-auto">
                            <span className="text-sm text-muted-foreground">Order</span>
                            <input type="number" value={item.order || 0} onChange={(e) => set("order", parseInt(e.target.value) || 0)} className="w-20 px-2 py-1 rounded-sm border border-border bg-background text-sm" />
                        </label>
                    </div>
                </div>
                <div className="p-6 border-t border-border flex justify-end gap-3">
                    <button onClick={onClose} className="px-5 py-2.5 rounded-sm border border-border text-sm">Cancel</button>
                    <button onClick={() => onSave(item)} className="inline-flex items-center gap-2 px-6 py-2.5 rounded-sm bg-primary text-primary-foreground text-sm font-medium" data-testid="job-save"><Save size={14} /> Save</button>
                </div>
            </div>
        </div>
    );
};

const F = ({ label, value, onChange, testid, placeholder }) => (
    <label className="block">
        <span className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-2 block">{label}</span>
        <input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} data-testid={testid} className="w-full px-3 py-2.5 rounded-sm border border-border bg-background text-sm" />
    </label>
);
const T = ({ label, value, onChange, rows = 3, testid }) => (
    <label className="block">
        <span className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-2 block">{label}</span>
        <textarea rows={rows} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="w-full px-3 py-2.5 rounded-sm border border-border bg-background text-sm resize-y" />
    </label>
);
